import { useCallback } from 'react';
import { AxiosResponse } from 'axios';
import { useQuery, useQueryClient } from 'react-query';

import axios from 'libs/Axios';
import {
  DISPLAY_TABLE_QUERY_KEY,
  DisplayTableParams,
} from 'services/v1/Tenant/AROKMS/DisplayTableService';
import { AxiosDefaultErrorEntity } from 'types/api/Common/ErrorTypes';
import { DisplayTableResponse } from 'types/api/Tenant/AROKMS/DisplayTableTypes';
import {
  DisplayTableV2FetchRequest,
  DisplayTableV2FetchResponse,
  DisplayTableV2RequestBuilderInput,
} from 'types/api/Tenant/AROKMS/DisplayTableV2Types';
import { buildDisplayTableV2Request, mapDisplayTableV2ResponseToLegacy } from './displayTableV2Adapter';

export type DisplayTableV2Params = DisplayTableParams &
  Omit<DisplayTableV2RequestBuilderInput, keyof DisplayTableParams | 'subjectId' | 'hierarchyFilters'>;

const DISPLAY_TABLE_V2_STALE_TIME = 50000;

function createDisplayTableV2Request(
  filter: DisplayTableV2Params,
  hierarchyFilters?: Record<string, string>
): DisplayTableV2FetchRequest {
  return buildDisplayTableV2Request({
    ...filter,
    subjectId: filter.subjectId === undefined ? '' : String(filter.subjectId),
    sortType: filter.sortType ?? undefined,
    hierarchyFilters,
  });
}

function getDisplayTableV2QueryKey(request: DisplayTableV2FetchRequest) {
  return [DISPLAY_TABLE_QUERY_KEY.DATA_LIST, 'v2', request];
}

function fetchDisplayTableV2(request: DisplayTableV2FetchRequest): Promise<AxiosResponse<DisplayTableResponse>> {
  return axios.post<DisplayTableV2FetchResponse>('/api/v2/display-table/fetch', request).then((response) => ({
    ...response,
    data: mapDisplayTableV2ResponseToLegacy(response.data),
  }));
}

export function useDisplayTableV2(
  filter: DisplayTableV2Params,
  hierarchyFilters?: Record<string, string>,
  enabled = true
) {
  const request = createDisplayTableV2Request(filter, hierarchyFilters);

  return useQuery<AxiosResponse<DisplayTableResponse>, AxiosDefaultErrorEntity>(
    getDisplayTableV2QueryKey(request),
    () => fetchDisplayTableV2(request),
    {
      enabled: enabled && !!filter.subjectId,
      keepPreviousData: true,
      staleTime: DISPLAY_TABLE_V2_STALE_TIME,
    }
  );
}

export function usePrefetchDisplayTableV2() {
  const queryClient = useQueryClient();

  return useCallback(
    (filter: DisplayTableV2Params, hierarchyFilters?: Record<string, string>) => {
      const request = createDisplayTableV2Request(filter, hierarchyFilters);
      return queryClient.prefetchQuery(
        getDisplayTableV2QueryKey(request),
        () => fetchDisplayTableV2(request),
        {
          staleTime: DISPLAY_TABLE_V2_STALE_TIME,
          meta: { suppressGlobalLoadingIndicator: true },
        }
      );
    },
    [queryClient]
  );
}
